import { Singleton } from '@denishussar/html5-game-engine';
import axios from 'axios';
import assign from 'assign-deep';
import Web3 from 'web3';

export type IBackendGameCarStateUpgrade = number | boolean;

export interface IBackendGameCarState {
    index: number;
    health: number;
    locked: boolean;
    color: number;
    unlockedColors: boolean[];
    upgrades: IBackendGameCarStateUpgrade[];
}

export interface IBackendGameState {
    name?: string;
    score?: number;
    highestScoreCar?: number;
    coins?: number;
    cars?: IBackendGameCarState[];
    currentCar?: number;
    unlockedLevels?: boolean[];
    unlockedAchievements?: boolean[];

    // score
    scoreDay?: number;
    scoreWeek?: number;
    scoreTotal?: number;
    coinsDay?: number;
    coinsWeek?: number;
    coinsTotal?: number;

    scoreDayTimestamp?: number;
    scoreWeekTimestamp?: number;

    scoreDayCar?: number;
    scoreWeekCar?: number;
}

export enum IBackendLeaderboardDurationTypeEnum {
    current,
    total,
}

export interface IBackendLeaderboardResult {
    top: IBackendLeaderboardItem[];
    currentPlayer?: IBackendLeaderboardItem;
}

export interface IBackendLeaderboardItem {
    user: string;
    nickname: string;
    position: string;
    length: string;
    coins: string;
}

@Singleton
export class Backend {
    private _enabled: boolean = false;

    private _web3: Web3;

    //private _url: string = 'https://racing-game-api.1inch.io';
    private _url: string = 'https://racing-game-api-staging.1inch.io';

    private _version: string = 'v1.0';

    private _message: string = 'Hi! You are logging in our amazing game';
    private _signature: string = '';

    private _address: string = 'a2';
    private _token: string = 't2';

    private _state: IBackendGameState = null;
    private _error: any | null = null;

    constructor() {
    }

    public setEnabled(value: boolean): void {
        this._enabled = value;
    }

    public isEnabled(): boolean {
        return this._enabled;
    }

    public get error() {
        const error = this._error;
        this._error = null;
        return error;
    }

    public async auth(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isEnabled()) {
                this._web3 = new Web3(Web3.givenProvider);

                window.ethereum.request({ method: 'eth_requestAccounts' }).then(addresses => addresses[0])
                    .then(address => {
                        this._address = address;

                        this.get('token/origin-data', {
                            'accept': 'application/json',
                        })
                            .then(({ data }) => {
                                this._message = data.originData;

                                //alert(`token/origin-data ${this._message} ${this._address}`);

                                // @ts-ignore
                                this._web3.eth.personal.sign(this._message, this._address)
                                    .then(signature => {
                                        this._signature = signature;

                                        //alert(`web3.eth.personal.sign ${this._signature}`);

                                        this.get('token', {
                                            'accept': 'application/json',
                                            'address': this._address,
                                            'signature': this._signature,
                                        })
                                            .then(({ data }) => {
                                                this._token = data.token;
                                                //alert(`token ${this._address} ${this._token}`);

                                                resolve();

                                            })
                                            .catch(error => {
                                                //alert(`token error ${JSON.stringify(error)}`);
                                                reject(error);
                                            });

                                    })
                                    .catch(error => {
                                        //alert(`sign error ${JSON.stringify(error)}`);
                                        reject(error);
                                    });

                            })
                            .catch(error => {
                                //alert(`token/origin-data error ${JSON.stringify(error)}`);
                                reject(error);
                            });
                    })
                    .catch(error => {
                        //alert(`request_accounts error ${JSON.stringify(error)}`);
                        reject(error);
                    });

            } else {
                resolve();
            }
        });
    }

    public saveState(state: IBackendGameState): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isEnabled()) {
                this.post('users/state', state)
                    .then(response => {
                        this._state = assign(this._state, state);
                        resolve();

                    })
                    .catch(error => {
                        //alert(JSON.stringify(error));
                        reject(error);
                    });

            } else {
                if (!this._state) {
                    this._state = this.getDefaultState();
                }
                this._state = assign(this._state, state);
                resolve();
            }
        });
    }

    public loadState(): Promise<IBackendGameState> {
        return new Promise((resolve, reject) => {
            if (this.isEnabled()) {
                this.get('users/state')
                    .then(async response => {
                        this._state = this._modifyStateWithProtocol(response.data.state);
                        resolve(this._state);

                    })
                    .catch(error => {
                        reject(error);
                    });

            } else {
                if (!this._state) {
                    this._state = this._modifyStateWithProtocol(this.getDefaultState());
                }
                resolve(this._state);
            }
        });
    }

    public getState(): IBackendGameState {
        return this._state;
    }

    private _modifyStateWithProtocol(state: IBackendGameState): IBackendGameState {
        const cars = state.cars;

        cars.forEach(carState => {
            let upgrades = carState.upgrades;

            // fix old protocol
            carState.upgrades = upgrades
                .map(value => +value)
                .map(value => isNaN(value) ? 0 : value);

            console.log('_modifyStateWithProtocol', carState.upgrades);
        });

        return state;
    }

    public getDefaultState(): IBackendGameState {
        return {
            cars: [
                {
                    index: 0,
                    health: 100,
                    locked: false,
                    color: 0,
                    unlockedColors: [true, false, false, false],
                    upgrades: [0, 0, 0, 0],
                },
                {
                    index: 1,
                    health: 100,
                    locked: true,
                    color: 0,
                    unlockedColors: [true, false, false, false],
                    upgrades: [0, 0, 0, 0],
                },
                {
                    index: 2,
                    health: 100,
                    locked: true,
                    color: 0,
                    unlockedColors: [true, false, false, false],
                    upgrades: [0, 0, 0, 0],
                },
                {
                    index: 3,
                    health: 100,
                    locked: true,
                    color: 0,
                    unlockedColors: [true, false, false, false],
                    upgrades: [0, 0, 0, 0],
                },
            ],
            coins: 60000,
            coinsDay: 0,
            coinsTotal: 0,
            coinsWeek: 0,
            currentCar: 0,
            highestScoreCar: 0,
            name: '',
            score: 0,
            scoreDay: 0,
            scoreDayCar: 0,
            scoreDayTimestamp: 0,
            scoreTotal: 0,
            scoreWeek: 0,
            scoreWeekCar: 0,
            scoreWeekTimestamp: 0,
            unlockedAchievements: [],
            unlockedLevels: [true, false],
        };
    }

    public getLeaderboard(total: boolean = false): Promise<IBackendLeaderboardResult> {
        return new Promise((resolve, reject) => {
            if (this.isEnabled()) {
                this.get(`leader-board-competition/leader-board${total ? '?total=true' : ''}`, {
                    'accept': '*/*',
                    'address': this._address,
                    'token': this._token,
                })
                    .then(response => resolve(response.data))
                    .catch(error => reject(error));
            } else {
                const getSample = (index, user = '0xfc7cea060f664ecc8df5438c802452e8726d9659') => {
                    return {
                        user: user,
                        nickname: 'user',
                        position: index,
                        length: index,
                        coins: index,
                    };
                };

                resolve({
                    'top': [],
                    /*
                    "currentPlayer": {
                    "user": "0xc4c276ad53053b98f750cdd195e14e6989c384fb",
                        "position": "5",
                        "length": "17444",
                        "coins": "176",
                        "nickname": "See3"
                    },
                    "top": [
                    {
                        "user": "0xe9ba60eefdba996a66a7f5415dda83573a70c492",
                        "position": "1",
                        "length": "434778",
                        "coins": "32856",
                        "nickname": "See1"
                    },
                    {
                        "user": "0xf1873b7a4120aefb08aa07dc38f03fcc313b3c1e",
                        "position": "2",
                        "length": "107175",
                        "coins": "6075",
                        "nickname": "VP"
                    },
                    {
                        "user": "0x3e1ca18b392e6e732a44bf5a7effeb4376f27803",
                        "position": "3",
                        "length": "58949",
                        "coins": "3592",
                        "nickname": "VP"
                    },
                    {
                        "user": "0x3c98ff8cc50bc98279449732db5b652baf3197a5",
                        "position": "4",
                        "length": "40619",
                        "coins": "2707",
                        "nickname": "j"
                    },
                    {
                        "user": "0xc4c276ad53053b98f750cdd195e14e6989c384fb",
                        "position": "5",
                        "length": "17444",
                        "coins": "176",
                        "nickname": "See3"
                    },
                    {
                        "user": "0x3ea60c21d9b39b0e0411762b56ac1336376ba991",
                        "position": "6",
                        "length": "17481",
                        "coins": "175",
                        "nickname": "See8"
                    },
                    {
                        "user": "0x14d7b8771e443805641db313dead1ba84eca3d76",
                        "position": "7",
                        "length": "9067",
                        "coins": "91",
                        "nickname": "See5"
                    },
                    {
                        "user": "0x04ad92f2847932537483dcf350ff5d6870933773",
                        "position": "8",
                        "length": "8877",
                        "coins": "89",
                        "nickname": "See7"
                    },
                    {
                        "user": "0x3a55d960444a8fc878dd0e95630a95258609a1aa",
                        "position": "9",
                        "length": "8786",
                        "coins": "88",
                        "nickname": "See6"
                    },
                    {
                        "user": "0x321eaa5d67254e9a178f3b6ace9f75957af15704",
                        "position": "10",
                        "length": "8796",
                        "coins": "88",
                        "nickname": "See2"
                    }
                ]
                */
                });
            }
        });
    }

    private get(url: string, headers: any = null): Promise<any> {
        return new Promise((resolve, reject) => {
            headers = headers ? headers : {
                'Content-Type': 'application/json',
                'address': this._address,
                'token': this._token,
            };

            axios.get(`${this._url}/${this._version}/${url}`, {
                headers: headers,
            })
                .then(function(response) {
                    console.log(`Request::get() ${url} success`, response);
                    resolve(response);
                })
                .catch(function(error) {
                    console.log(`Request::get() ${url} error`, error);
                    reject(error);
                });
        });
    }

    private post(url: string, data: any): Promise<any> {
        return new Promise((resolve, reject) => {
            axios.post(`${this._url}/${this._version}/${url}`, { state: data }, {
                headers: {
                    'accept': '*/*',
                    'address': this._address,
                    'token': this._token,
                    'Content-Type': 'application/json',
                },
            })
                .then(function(response) {
                    resolve(response);
                    console.log(`Request::post() ${url} success`, response);
                })
                .catch(function(error) {
                    console.log(`Request::post() ${url} error`, error);
                    reject(error);
                });
        });
    }

    public sendComplete(startTimestamp: number, endTimestamp: number, levelId: number, carId: number, coins: number, score: number): void {
        if (!this.isEnabled()) {
            return;
        }

        try {
            //@ts-ignore
            window.webkit.messageHandlers.rootAppCallback.postMessage({
                'a': startTimestamp,
                'b': endTimestamp,
                'c': this._address,
                'd': levelId,
                'e': carId,
                'f': coins,
                'g': score,
                'z': this._token,
            });
        } catch (e) {
            this._error = e;
        }
    }
}
